/**
 * Page publique de traçabilité — accessible SANS authentification
 * URL : /trace/:lotCode (encodée dans le QR code de chaque sac)
 *
 * Affiche les informations vérifiables d'un lot pour acheteurs/consommateurs.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Leaf, MapPin, Award, Users, CalendarDays, Package, ShieldCheck,
  TreePine, BarChart3, Loader2, AlertTriangle, CheckCircle2,
  Sprout, Factory, Truck, Globe, Hash, ExternalLink,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_ICONS = {
  'Recolte': Sprout,
  'Récolte': Sprout,
  'Fermentation': Factory,
  'Sechage': Factory,
  'Séchage': Factory,
  'Stockage Cooperative': Package,
  'Stockage Coopérative': Package,
  'Conditionnement': Package,
  'Transport': Truck,
  'Export': Globe,
};

const STATUT_LABELS = {
  en_stock: { label: 'En stock', color: 'bg-blue-100 text-blue-700' },
  vendu: { label: 'Vendu', color: 'bg-amber-100 text-amber-700' },
  exporte: { label: 'Exporté', color: 'bg-emerald-100 text-emerald-700' },
};

const PublicTracePage = () => {
  const { lotCode } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/trace/${lotCode}`);
        if (!res.ok) {
          throw new Error(res.status === 404 ? 'Lot introuvable' : 'Erreur de chargement');
        }
        const json = await res.json();
        setData(json);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [lotCode]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F2818] via-[#1A3622] to-[#1A3622]">
        <div className="text-center text-white">
          <Loader2 className="h-10 w-10 animate-spin mx-auto mb-3" />
          <p className="text-sm opacity-80">Vérification de la traçabilité…</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F2818] to-[#1A3622] p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-md p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-[#1A3622] mb-1">Lot non vérifiable</h1>
          <p className="text-sm text-[#6B7280] mb-4">{error || 'Ce code lot est inconnu ou a été désactivé.'}</p>
          <p className="font-mono text-xs text-[#9CA3AF] bg-[#F3F4F6] rounded px-2 py-1 inline-block">{lotCode}</p>
        </div>
      </div>
    );
  }

  const score = data.score_durabilite || {};
  const statut = STATUT_LABELS[data.statut_commercial] || STATUT_LABELS.en_stock;
  const scorePct = Math.min(100, Math.max(0, score.score_moyen || 0));

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8FAF6] to-white" data-testid="public-trace-page">
      {/* Top brand bar */}
      <div className="bg-gradient-to-r from-[#0F2818] via-[#1A3622] to-[#2d5a4d] text-white py-3 px-4 sticky top-0 z-10 shadow-md">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Leaf className="h-5 w-5 text-emerald-300" />
            <div>
              <p className="text-xs font-bold tracking-wide">GreenLink Agritech</p>
              <p className="text-[10px] opacity-70">Traçabilité ARS 1000 — Côte d'Ivoire</p>
            </div>
          </div>
          {data.certifie_ars1000 && (
            <div className="flex items-center gap-1 bg-emerald-500/20 border border-emerald-400/40 rounded-full px-2 py-1">
              <ShieldCheck className="h-3 w-3 text-emerald-300" />
              <span className="text-[10px] font-bold text-emerald-100">Certifié ARS 1000</span>
            </div>
          )}
          {data.immutable_ledger?.status === 'verified' && (
            <div className="flex items-center gap-1 bg-emerald-400/30 border border-emerald-300/60 rounded-full px-2 py-1 shadow-sm" data-testid="badge-blockchain-verified">
              <ShieldCheck className="h-3 w-3 text-white" />
              <span className="text-[10px] font-bold text-white tracking-wide">Vérifié sur Blockchain</span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 md:py-8 space-y-5">
        {/* Hero — Lot info */}
        <div className="bg-white rounded-2xl shadow-lg border border-[#E5E5E0] overflow-hidden">
          <div className="bg-gradient-to-br from-[#1A3622] to-[#2d5a4d] text-white px-6 py-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-[10px] uppercase tracking-widest opacity-70">Lot vérifié</p>
                <h1 className="text-xl md:text-2xl font-bold font-mono tracking-tight mt-0.5" data-testid="lot-code">
                  {data.lot_code}
                </h1>
                <p className="text-xs opacity-80 mt-1">Campagne {data.campagne || '—'}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-[11px] font-bold ${statut.color}`} data-testid="statut-commercial">
                {statut.label}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#E5E5E0]">
            <Cell icon={Package} label="Quantité" value={`${(data.quantite_kg || 0).toLocaleString('fr-FR')} kg`} />
            <Cell icon={CalendarDays} label="Récolte" value={data.date_recolte || '—'} />
            <Cell icon={Users} label="Planteurs" value={data.nb_contributeurs || 0} />
            <Cell icon={MapPin} label="Village" value={data.village_principal || data.villages?.[0] || '—'} />
          </div>
        </div>

        {/* Coopérative */}
        <div className="bg-white rounded-2xl shadow-sm border border-[#E5E5E0] p-5">
          <p className="text-[10px] uppercase tracking-widest text-[#6B7280] mb-2">Coopérative d&apos;origine</p>
          <div className="flex items-center gap-4">
            {data.cooperative?.logo_url && (
              <img
                src={data.cooperative.logo_url}
                alt={`Logo ${data.cooperative?.nom || ''}`}
                className="h-14 w-14 rounded-md object-cover border border-[#E5E5E0] shrink-0"
                data-testid="coop-logo"
              />
            )}
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-[#1A3622] truncate">{data.cooperative?.nom || '—'}</h2>
              {(data.cooperative?.village || data.cooperative?.region) && (
                <p className="text-xs text-[#6B7280] mt-1 flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {[data.cooperative?.village, data.cooperative?.region].filter(Boolean).join(' · ')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Score durabilité */}
        <div className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl shadow-sm border border-emerald-100 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Award className="h-5 w-5 text-emerald-600" />
              <h2 className="text-sm font-bold text-[#1A3622]">Score durabilité</h2>
            </div>
            <span className="text-2xl font-bold text-emerald-700" data-testid="score-moyen">
              {score.score_moyen || 0}<span className="text-sm font-normal text-[#6B7280]">/100</span>
            </span>
          </div>
          <div className="h-2.5 bg-[#F3F4F6] rounded-full overflow-hidden mb-4">
            <div
              className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all"
              style={{ width: `${scorePct}%` }}
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Stat icon={Sprout} label="Rendement moyen" value={`${score.rendement_moyen || 0} kg/ha`} />
            <Stat icon={TreePine} label="Arbres ombrage/ha" value={`${score.arbres_par_ha_moyen || 0}`} hint="cible : 30" />
            <Stat icon={BarChart3} label="Couverture données" value={score.couverture || '—'} />
          </div>
        </div>

        {/* Planteurs anonymisés */}
        <div className="bg-white rounded-2xl shadow-sm border border-[#E5E5E0] p-5">
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-[#1A3622]" />
            <h2 className="text-sm font-bold text-[#1A3622]">Planteurs contributeurs</h2>
            <span className="text-[10px] text-[#9CA3AF] ml-auto italic">(noms anonymisés)</span>
          </div>
          {data.contributeurs_anonymises?.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {data.contributeurs_anonymises.map((c, i) => (
                <div key={i} className="bg-[#F9FAFB] border border-[#E5E5E0] rounded-lg px-3 py-2">
                  <p className="text-sm font-bold text-[#1A3622] font-mono">{c.initiales || '—'}</p>
                  <p className="text-[10px] text-[#6B7280] truncate">{c.village || '—'}</p>
                  <p className="text-[10px] text-emerald-700 font-medium">{c.quantite_kg.toFixed(0)} kg</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-[#9CA3AF] italic">—</p>
          )}
        </div>

        {/* Blockchain — vérifié OU standard */}
        {data.immutable_ledger?.status === 'verified' ? (
          <div className="bg-white rounded-2xl shadow-lg border-2 border-emerald-300 overflow-hidden" data-testid="ledger-section">
            <div className="bg-gradient-to-r from-emerald-600 via-emerald-700 to-emerald-900 text-white px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 border border-white/30 flex items-center justify-center">
                  <ShieldCheck className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-sm font-bold tracking-wide">Vérifié sur Blockchain</h2>
                  <p className="text-[10px] opacity-80">Preuve cryptographique immuable</p>
                </div>
              </div>
              <span className="text-[10px] bg-white/20 border border-white/40 rounded-full px-2.5 py-1 font-bold tracking-wide flex items-center gap-1.5">
                {(() => {
                  // Détermine le réseau : depuis network, ou parsé depuis ledger_url
                  const lu = data.immutable_ledger.ledger_url || '';
                  const net = (data.immutable_ledger.network || '').toLowerCase()
                    || (lu.includes('/mainnet/') ? 'mainnet'
                        : lu.includes('/testnet/') ? 'testnet'
                        : lu.includes('/previewnet/') ? 'previewnet'
                        : '');
                  const prov = (data.immutable_ledger.provider || '').toLowerCase();
                  const providerLabel = prov === 'hedera' ? 'Hedera'
                    : prov === 'polygon' ? 'Polygon'
                    : prov === 'simulation' ? 'Simulation'
                    : prov ? prov.charAt(0).toUpperCase() + prov.slice(1)
                    : 'Blockchain';
                  if (net === 'mainnet') {
                    return (
                      <span data-testid="ledger-network-badge" className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse"></span>
                        {providerLabel} · Mainnet
                      </span>
                    );
                  }
                  if (net === 'testnet' || net === 'previewnet') {
                    return (
                      <span data-testid="ledger-network-badge" className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-300"></span>
                        {providerLabel} · {net.charAt(0).toUpperCase() + net.slice(1)}
                      </span>
                    );
                  }
                  return (
                    <span data-testid="ledger-network-badge">AUTHENTIQUE</span>
                  );
                })()}
              </span>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-[11px] text-[#6B7280] leading-relaxed">
                Une empreinte cryptographique unique de ce lot a été générée et inscrite sur la couche de traçabilité GreenLink. Toute modification ultérieure des données du lot invaliderait cette empreinte.
              </p>
              {/* Note explicative sur le réseau */}
              {(() => {
                const lu = data.immutable_ledger.ledger_url || '';
                const net = (data.immutable_ledger.network || '').toLowerCase()
                  || (lu.includes('/testnet/') ? 'testnet'
                      : lu.includes('/previewnet/') ? 'previewnet'
                      : '');
                if (net === 'testnet' || net === 'previewnet') {
                  return (
                    <div data-testid="ledger-network-note" className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 leading-relaxed">
                      <strong>Phase pilote :</strong> ce lot est ancré sur le réseau public Hedera Testnet (équivalent technique du mainnet, sans frais). Migration vers Hedera Mainnet prévue après validation des partenariats coopératives.
                    </div>
                  );
                }
                return null;
              })()}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] mb-1 flex items-center gap-1">
                    <Hash className="h-3 w-3" /> Empreinte SHA-256
                  </p>
                  <code className="block text-[9px] font-mono text-[#1A3622] bg-emerald-50 border border-emerald-100 rounded px-2 py-2 break-all" data-testid="public-ledger-hash">
                    {data.immutable_ledger.hash}
                  </code>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] mb-1">Identifiant de transaction</p>
                  <code className="block text-[10px] font-mono text-[#1A3622] bg-emerald-50 border border-emerald-100 rounded px-2 py-2 break-all" data-testid="public-ledger-tx">
                    {data.immutable_ledger.tx_id}
                  </code>
                </div>
              </div>
              <div className="flex items-center justify-between text-[10px] text-[#6B7280] pt-1 flex-wrap gap-2">
                <span>
                  Enregistré le{' '}
                  {data.immutable_ledger.registered_at
                    ? new Date(data.immutable_ledger.registered_at).toLocaleDateString('fr-FR', {
                        day: 'numeric', month: 'long', year: 'numeric',
                      })
                    : '—'}
                </span>
                {data.immutable_ledger.ledger_url && (
                  <a
                    href={data.immutable_ledger.ledger_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-700 underline inline-flex items-center gap-1"
                  >
                    Explorateur <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-[#E5E5E0] p-5 flex items-start gap-3" data-testid="ledger-standard">
            <div className="w-10 h-10 rounded-full bg-[#F3F4F6] flex items-center justify-center shrink-0">
              <ShieldCheck className="h-5 w-5 text-[#9CA3AF]" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-[#1A3622]">Traçabilité standard</h2>
              <p className="text-[11px] text-[#6B7280] mt-1 leading-relaxed">
                Ce lot est tracé selon le standard ARS 1000. La preuve sur Blockchain n'a pas (encore) été générée pour ce lot.
              </p>
            </div>
          </div>
        )}


        {/* Timeline */}
        {data.timeline?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-[#E5E5E0] p-5">
            <h2 className="text-sm font-bold text-[#1A3622] mb-4">Chaîne de traçabilité</h2>
            <div className="space-y-3">
              {data.timeline.map((ev, i) => {
                const Icon = STAGE_ICONS[ev.etape] || CheckCircle2;
                return (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4 text-emerald-700" />
                    </div>
                    <div className="flex-1 pt-1">
                      <p className="text-xs font-bold text-[#1A3622]">{ev.etape}</p>
                      <p className="text-[11px] text-[#6B7280]">
                        {ev.date || '—'} {ev.lieu ? `· ${ev.lieu}` : ''}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer trust */}
        <div className="text-center py-6">
          <p className="text-[10px] text-[#9CA3AF] flex items-center justify-center gap-1.5">
            <ShieldCheck className="h-3 w-3" />
            Vérifié le {new Date(data.verified_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
          <p className="text-[10px] text-[#9CA3AF] mt-2">
            Plateforme GreenLink Agritech · Norme ARS 1000-2 · Côte d'Ivoire
          </p>
        </div>
      </div>
    </div>
  );
};

const Cell = ({ icon: Icon, label, value }) => (
  <div className="bg-white px-4 py-3 text-center">
    <Icon className="h-4 w-4 text-emerald-600 mx-auto mb-1" />
    <p className="text-[9px] uppercase tracking-wide text-[#9CA3AF]">{label}</p>
    <p className="text-sm font-bold text-[#1A3622] mt-0.5 truncate">{value}</p>
  </div>
);

const Stat = ({ icon: Icon, label, value, hint }) => (
  <div className="bg-white border border-[#E5E5E0] rounded-lg p-3">
    <Icon className="h-3.5 w-3.5 text-emerald-600 mb-1" />
    <p className="text-[10px] text-[#6B7280] leading-tight">{label}</p>
    <p className="text-sm font-bold text-[#1A3622] mt-0.5">{value}</p>
    {hint && <p className="text-[9px] text-[#9CA3AF] italic mt-0.5">{hint}</p>}
  </div>
);

export default PublicTracePage;
